import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Button } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import API, { OrchestratorAPI } from "../../common/api/axios";
import { TENANT_HEADERS } from "../../common/config";
import { formatSlotDisplay } from "../../common/utils/slot";
import {
  cycleCountDelta,
  matchesExpectedIdentifiers,
} from "../../common/utils/cycleCount";
import { cleanScan } from "../../common/utils/scan";
import { isAlreadyCompleteError, serverErrorMessage } from "../../common/utils/errors";
import { toastError, toastSuccess } from "../../common/utils/toast";
import StepShell from "../../components/StepShell";
import StatusBanner from "../../components/StatusBanner";
import ScanField from "../../components/ScanField";
import QuantityStepper from "../../components/QuantityStepper";
import ConfirmModal from "../../components/ConfirmModal";
import { colors } from "../../app/styles";

const ORCH_POLL_MS = 5000;
const TASK_POLL_MS = 3000;

interface CycleCountTask {
  id: string;
  isComplete?: boolean;
  isCancelled?: boolean;
  isAborted?: boolean;
  cycleCountTask: {
    binId: string;
    skuId: string;
    batchId?: string;
    expectedQuantity: number;
    gateLocation: { x: number; y: number; z: number };
  };
}

const isActiveCcTask = (task: any): boolean =>
  !!task?.id &&
  !!task?.cycleCountTask &&
  task?.isComplete !== true &&
  task?.isCancelled !== true &&
  task?.isAborted !== true;

const Center = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 20px;
  text-align: center;
  color: ${colors.textMuted};
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TaskCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 72px;
  padding: 12px 14px;
  border-radius: 12px;
  text-align: left;
  background: #ffffff;
  border: 1px solid ${colors.cardBorder};
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.06);
  cursor: pointer;

  &:active {
    transform: scale(0.99);
  }
`;

const TaskBody = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const TaskSlot = styled.div`
  font-weight: 800;
  font-size: 16px;
  color: ${colors.primaryDark};
`;

const TaskMeta = styled.div`
  font-size: 13px;
  color: ${colors.textMuted};
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled.span`
  flex: 0 0 auto;
  font-size: 22px;
  color: ${colors.textMuted};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: ${colors.primaryDark};
`;

const FieldCard = styled.div`
  border: 1px solid ${colors.cardBorder};
  border-radius: 12px;
  background: #ffffff;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ExpectedRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const ExpectedLabel = styled.span`
  font-size: 13px;
  color: ${colors.textMuted};
`;

const ExpectedValue = styled.span`
  font-size: 18px;
  font-weight: 800;
  color: ${colors.primaryDark};
  text-align: right;
  overflow-wrap: anywhere;
`;

const QtyExpected = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;

  strong {
    font-size: 34px;
    font-weight: 800;
    color: ${colors.primaryDark};
    line-height: 1;
  }
  span {
    font-size: 15px;
    color: ${colors.textMuted};
  }
`;

const ErrorPanel = styled.div`
  border: 1px solid #ffa39e;
  background: #fff1f0;
  border-radius: 12px;
  padding: 12px 14px;
  color: #a8071a;
`;

const ErrorTitle = styled.div`
  font-weight: 800;
  font-size: 15px;
  letter-spacing: 0.04em;
`;

const SCAN_ERROR = {
  title: "ERROR DETECTED",
  headline: "Discrepancy in mapped Bin or SKU ID.",
  description:
    "The scanned data does not exist or does not match the active task.",
};

type Mode = "list" | "count";
type CountField = "bin" | "sku" | "qty";

export default function CycleCount() {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<CycleCountTask[]>([]);

  const [mode, setMode] = useState<Mode>("list");
  const [selectedTask, setSelectedTask] = useState<CycleCountTask | null>(null);
  const [binScan, setBinScan] = useState("");
  const [skuScan, setSkuScan] = useState("");
  const [countField, setCountField] = useState<CountField>("bin");
  const [countedQuantity, setCountedQuantity] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [scanError, setScanError] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const mountedRef = useRef(true);

  const checkOrchestration = useCallback(async () => {
    try {
      const res = await OrchestratorAPI.get("orchestrations", {
        headers: TENANT_HEADERS,
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        const activeOrch = res.data.find(
          (orch: any) =>
            orch.operationMode === "CycleCounting" &&
            orch.status === "StepExecuted" &&
            new Date(orch.creationDate) >= oneDayAgo
        );
        if (mountedRef.current) {
          setActive(!!activeOrch);
        }
      }
    } catch {
      if (mountedRef.current) {
        setActive(false);
      }
    }
  }, []);

  const fetchTasks = useCallback(async (): Promise<CycleCountTask[] | null> => {
    try {
      const res = await API.get("PickStation/cycleCountTasks", {
        headers: TENANT_HEADERS,
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        if (mountedRef.current) {
          setTasks(res.data);
        }
        return res.data;
      }
    } catch {
      // keep prior tasks on transient error
    }
    return null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    checkOrchestration().finally(() => {
      if (mountedRef.current) {
        setLoading(false);
      }
    });
    return () => {
      mountedRef.current = false;
    };
  }, [checkOrchestration]);

  useEffect(() => {
    const interval = setInterval(checkOrchestration, ORCH_POLL_MS);
    return () => clearInterval(interval);
  }, [checkOrchestration]);

  useEffect(() => {
    if (!active) {
      setTasks([]);
      return;
    }
    fetchTasks();
    const interval = setInterval(fetchTasks, TASK_POLL_MS);
    return () => clearInterval(interval);
  }, [active, fetchTasks]);

  const activeTasks = tasks.filter(isActiveCcTask);

  const resetToList = () => {
    setMode("list");
    setSelectedTask(null);
    setBinScan("");
    setSkuScan("");
    setCountField("bin");
    setScanError(false);
    setShowConfirm(false);
  };

  const selectTask = (task: CycleCountTask) => {
    setSelectedTask(task);
    setCountedQuantity(task.cycleCountTask.expectedQuantity ?? 0);
    setBinScan("");
    setSkuScan("");
    setCountField("bin");
    setScanError(false);
    setShowConfirm(false);
    setMode("count");
  };

  // After the SKU is scanned we deliberately do NOT submit — the operator may
  // still need to adjust the counted quantity. Blur the scan field so the wedge's
  // trailing Enter can't activate a focused button, then make the quantity stepper
  // the active step. Submitting now requires an explicit "Verify & Submit" tap.
  const handleSkuScanned = () => {
    (document.activeElement as HTMLElement | null)?.blur();
    setCountField("qty");
  };

  // Once a count is submitted, jump straight into the next pending task instead of
  // parking on a success screen. Fresh tasks are re-fetched so the count we just
  // finished is gone; when nothing is left to count we drop back to the task list.
  const advanceAfterSubmit = async (completedTaskId: string) => {
    const latest = (await fetchTasks()) ?? tasks;
    const next = latest
      .filter(isActiveCcTask)
      .find((task) => task.id !== completedTaskId);
    if (!mountedRef.current) {
      return;
    }
    if (next) {
      selectTask(next);
    } else {
      resetToList();
    }
  };

  // "Verify & Submit" runs the instant client-side equality check, then opens the
  // review modal. The scanned bin/sku must match the task before the operator is
  // shown a review to confirm — a mismatch surfaces the error inline instead.
  const openReview = () => {
    if (!selectedTask) {
      return;
    }
    const bin = cleanScan(binScan);
    const sku = cleanScan(skuScan);
    const expectedBin = selectedTask.cycleCountTask.binId;
    const expectedSku = selectedTask.cycleCountTask.skuId;
    if (!matchesExpectedIdentifiers(bin, sku, expectedBin, expectedSku)) {
      setScanError(true);
      return;
    }
    setScanError(false);
    setShowConfirm(true);
  };

  // Modal "Confirm": server-side existence (both must be 200), then complete the
  // count. On any failure the modal closes and the error surfaces on the page.
  const handleConfirm = async () => {
    if (!selectedTask) {
      return;
    }
    const bin = cleanScan(binScan);
    const sku = cleanScan(skuScan);

    setSubmitting(true);
    try {
      const [binRes, skuRes] = await Promise.all([
        API.get(`BinMaster/getById/${encodeURIComponent(bin)}`, {
          headers: TENANT_HEADERS,
          validateStatus: () => true,
        }),
        API.get(`MasterData/sku/${encodeURIComponent(sku)}`, {
          headers: TENANT_HEADERS,
          validateStatus: () => true,
        }),
      ]);
      if (binRes.status !== 200 || skuRes.status !== 200) {
        setShowConfirm(false);
        setScanError(true);
        return;
      }

      const delta = cycleCountDelta(
        countedQuantity,
        selectedTask.cycleCountTask.expectedQuantity ?? 0
      );
      await API.put(
        `PickStation/completeCycleCount/${encodeURIComponent(
          selectedTask.id
        )}/${delta}`,
        null,
        { headers: TENANT_HEADERS }
      );
      setShowConfirm(false);
      toastSuccess("Cycle count verified");
      await advanceAfterSubmit(selectedTask.id);
    } catch (error) {
      setShowConfirm(false);
      if (isAlreadyCompleteError(error)) {
        toastSuccess("Already completed — refreshing");
        await advanceAfterSubmit(selectedTask.id);
      } else {
        toastError(
          serverErrorMessage(error, "Failed to complete cycle count")
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──

  if (loading) {
    return <Center>Checking cycle count operation…</Center>;
  }

  if (!active) {
    return (
      <Center>
        <StatusBanner tone="info">
          No active cycle count operation. Start one from the desktop Operator
          UI, then the tasks will appear here.
        </StatusBanner>
        <Button size="large" onClick={() => navigate("/")}>
          Home
        </Button>
      </Center>
    );
  }

  if (mode === "count" && selectedTask) {
    const cc = selectedTask.cycleCountTask;
    const bothScanned =
      cleanScan(binScan) !== "" && cleanScan(skuScan) !== "";
    return (
      <StepShell
        title="Verify Count"
        subtitle={`Slot ${formatSlotDisplay(cc.gateLocation)}`}
        onBack={resetToList}
        actions={
          <>
            <Button size="large" onClick={resetToList} disabled={submitting}>
              Cancel
            </Button>
            <Button
              size="large"
              type="primary"
              disabled={!bothScanned}
              onClick={openReview}
            >
              Verify &amp; Submit
            </Button>
          </>
        }
      >
        {scanError && (
          <ErrorPanel>
            <ErrorTitle>{SCAN_ERROR.title}</ErrorTitle>
            <div style={{ fontWeight: 600, marginTop: 4 }}>
              {SCAN_ERROR.headline}
            </div>
            <div style={{ fontSize: 13, marginTop: 2 }}>
              {SCAN_ERROR.description}
            </div>
          </ErrorPanel>
        )}

        {/* Each field shows its expected value inline, mirroring the desktop
            Operator UI (expected value above the input the operator fills). */}
        <FieldGroup>
          <FieldTitle>Scan Tote ID</FieldTitle>
          <FieldCard>
            <ExpectedRow>
              <ExpectedLabel>Expected Bin ID</ExpectedLabel>
              <ExpectedValue>{cc.binId}</ExpectedValue>
            </ExpectedRow>
            <ScanField
              placeholder="TOTE…"
              value={binScan}
              onChange={setBinScan}
              onScan={() => setCountField("sku")}
              validateState={scanError ? "error" : "idle"}
              autoFocus={countField === "bin"}
              focusSignal={countField}
              disabled={submitting}
            />
          </FieldCard>
        </FieldGroup>

        <FieldGroup>
          <FieldTitle>Scan SKU ID</FieldTitle>
          <FieldCard>
            <ExpectedRow>
              <ExpectedLabel>Expected SKU ID</ExpectedLabel>
              <ExpectedValue>{cc.skuId}</ExpectedValue>
            </ExpectedRow>
            <ScanField
              placeholder="SKU…"
              value={skuScan}
              onChange={setSkuScan}
              onScan={handleSkuScanned}
              validateState={scanError ? "error" : "idle"}
              autoFocus={countField === "sku"}
              focusSignal={countField}
              disabled={submitting}
            />
          </FieldCard>
        </FieldGroup>

        <FieldGroup>
          <FieldTitle>Counted Quantity</FieldTitle>
          <FieldCard>
            <QtyExpected>
              <strong>{cc.expectedQuantity}</strong>
              <span>Expected</span>
            </QtyExpected>
            <QuantityStepper
              value={countedQuantity}
              onChange={setCountedQuantity}
              disabled={submitting}
              active={countField === "qty"}
            />
          </FieldCard>
        </FieldGroup>

        <FieldGroup>
          <FieldTitle>Batch</FieldTitle>
          <FieldCard>
            <ExpectedRow>
              <ExpectedLabel>Expected Batch</ExpectedLabel>
              <ExpectedValue>{cc.batchId?.trim() || "—"}</ExpectedValue>
            </ExpectedRow>
          </FieldCard>
        </FieldGroup>

        <ConfirmModal
          open={showConfirm}
          icon={<SyncOutlined />}
          title="Confirm Cycle Count"
          subtitle="Verify counted metrics against database parameters"
          rows={[
            { label: "Location Slot", value: formatSlotDisplay(cc.gateLocation) },
            { label: "Bin ID", value: cc.binId },
            { label: "SKU Code", value: cc.skuId },
            {
              label: "Batch ID",
              value: cc.batchId?.trim() || "—",
              highlight: true,
            },
            { label: "Qty Counted", value: countedQuantity, highlight: true },
          ]}
          note="Pickstation untilts and awaits bot retrieval on confirmation."
          confirmLabel="Confirm"
          loading={submitting}
          onCancel={() => setShowConfirm(false)}
          onConfirm={handleConfirm}
        />
      </StepShell>
    );
  }

  // mode === "list"
  return (
    <StepShell
      title="Cycle Count"
      subtitle="Select a task to verify"
      onBack={() => navigate("/")}
    >
      {activeTasks.length === 0 ? (
        <Center>No cycle count tasks.</Center>
      ) : (
        <TaskList>
          {activeTasks.map((task) => {
            const cc = task.cycleCountTask;
            return (
              <TaskCard
                key={task.id}
                type="button"
                onClick={() => selectTask(task)}
              >
                <TaskBody>
                  <TaskSlot>Slot {formatSlotDisplay(cc.gateLocation)}</TaskSlot>
                  <TaskMeta>
                    Bin {cc.binId} · SKU {cc.skuId}
                  </TaskMeta>
                  <TaskMeta>
                    Batch {cc.batchId?.trim() || "—"} · Expected{" "}
                    {cc.expectedQuantity}
                  </TaskMeta>
                </TaskBody>
                <Chevron>›</Chevron>
              </TaskCard>
            );
          })}
        </TaskList>
      )}
    </StepShell>
  );
}
